import { UserRole, User } from 'shared/types';

/**
 * Role Categories
 *
 * After the 2026-05 unification the platform has 6 roles. The "Contractor"
 * dimension is no longer encoded as a role — it's the user's `Company.companyType`
 * (cliente | proveedor) plus their `companies[].isExternal` flag.
 *
 * - PLATFORM: System-wide (Super Admin, Soporte Monitor)
 * - CLIENT: Company/organization staff (Administrador, Approver, Personal)
 * - OPERATIONS: Field operations (Security)
 *
 * Helpers that need to ask "is this user a contractor?" should look at the
 * active company's `companyType` rather than the role string.
 */

// Platform roles - System administrators
export const PLATFORM_ROLES = [
  UserRole.SUPER_ADMIN,
  UserRole.SAFETY_STAFF,
] as const;

// Client roles - Company/organization staff. Includes provider-side users:
// after unification, working at a `proveedor` company is a company-type
// distinction, not a role distinction.
export const CLIENT_ROLES = [
  UserRole.COMPANY_ADMIN,
  UserRole.CLIENT_APPROVER,
  UserRole.CLIENT_STAFF,
] as const;

// Operations roles - Field validators
export const OPERATIONS_ROLES = [
  UserRole.SECURITY,
] as const;

// Approver roles - Work permit approvers only
export const APPROVER_ROLES = [
  UserRole.CLIENT_APPROVER,
] as const;

// All roles that can manage their company's team
export const TEAM_MANAGEMENT_ROLES = [
  UserRole.COMPANY_ADMIN,
  UserRole.CLIENT_APPROVER,
] as const;

/**
 * Roles that never need to satisfy per-company verifications. The BE's
 * `userVerificationStatusService` already auto-verifies these via its
 * own short-circuits (super_admin / safety_staff are auto-verified;
 * client_approver is auto-verified because they review docs but don't
 * carry their own; security has no compliance docs in the current
 * model). FE tables that render a "Verificación" chip should treat
 * these roles as not-applicable rather than red-X "unverified".
 *
 * `User.verificationStatus` on the document defaults to `'unverified'`
 * until something explicitly recomputes it — for these roles that
 * default is misleading, so the chip needs a role-aware short-circuit.
 */
export const ROLES_WITHOUT_VERIFICATIONS = [
  UserRole.SUPER_ADMIN,
  UserRole.SAFETY_STAFF,
  UserRole.COMPANY_ADMIN,
  UserRole.CLIENT_APPROVER,
  UserRole.SECURITY,
] as const;

/**
 * True when this role doesn't require per-company verifications.
 * Accepts undefined so callers can pass `getEffectiveRole(...)` without
 * a null check.
 */
export const roleNeedsVerifications = (role: UserRole | undefined | null): boolean => {
  if (!role) return false;
  return !(ROLES_WITHOUT_VERIFICATIONS as readonly UserRole[]).includes(role);
};

// All roles that have access to training/courses
// Note: Platform roles (SUPER_ADMIN, SAFETY_STAFF) don't need "Mis Cursos" menu
export const TRAINING_ACCESS_ROLES = [
  UserRole.COMPANY_ADMIN,
  UserRole.CLIENT_APPROVER,
  UserRole.CLIENT_STAFF,
] as const;

// Roles with admin-level access (platform + company_admin)
export const ADMIN_ROLES = [
  UserRole.SUPER_ADMIN,
  UserRole.SAFETY_STAFF,
  UserRole.COMPANY_ADMIN,
] as const;

// Roles that can approve work permits
export const WORK_PERMIT_APPROVAL_ROLES = [
  UserRole.SUPER_ADMIN,
  UserRole.SAFETY_STAFF,
  UserRole.COMPANY_ADMIN,
  UserRole.CLIENT_APPROVER,
  UserRole.SECURITY,
] as const;

/**
 * Check if a role belongs to the Platform category
 */
export const isPlatformRole = (role: UserRole): boolean => {
  return (PLATFORM_ROLES as readonly UserRole[]).includes(role);
};

/**
 * Check if a role belongs to the Client category. (Post-unification this also
 * covers provider-side users — distinguish via Company.companyType.)
 */
export const isClientRole = (role: UserRole): boolean => {
  return (CLIENT_ROLES as readonly UserRole[]).includes(role);
};

/**
 * Check if a role belongs to the Operations category
 */
export const isOperationsRole = (role: UserRole): boolean => {
  return (OPERATIONS_ROLES as readonly UserRole[]).includes(role);
};

/**
 * Check if a role is company-scoped (belongs to a specific company)
 * These roles should have access to their company's data by default
 */
export const isCompanyRole = (role: UserRole): boolean => {
  return isClientRole(role);
};

/**
 * Check if a role has admin-level access (platform admins + company_admin)
 */
export const isAdminRole = (role: UserRole): boolean => {
  return (ADMIN_ROLES as readonly UserRole[]).includes(role);
};

/**
 * Check if a role has global access (can see all companies, global settings, etc.)
 */
export const hasGlobalAccess = (role: UserRole): boolean => {
  return (PLATFORM_ROLES as readonly UserRole[]).includes(role);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (
  userRole: UserRole | UserRole[],
  allowedRoles: readonly UserRole[]
): boolean => {
  const roles = Array.isArray(userRole) ? userRole : [userRole];
  return roles.some(role => allowedRoles.includes(role));
};

/**
 * Is `user` the (active, non-external) company_admin of `companyId` —
 * regardless of which company is currently active in AuthContext?
 *
 * A user's `role` from `hasRole()`/`AuthContext.user.role` reflects only the
 * currently-active company entry. Someone who is company_admin of their own
 * provider company but was added as a worker (`isExternal`) to a client gets
 * capped to client_staff on that client entry (see
 * capRoleForExternalEntry / EXTERNAL_COMPANY_ADMIN_CONFLICT on the BE) — so
 * while browsing "as" that client's colaborador, `hasRole([COMPANY_ADMIN])`
 * reads false even though they're still their own company's admin. The BE
 * mirrors this with `isHomeCompanyAdminOf`, keyed off the specific company
 * in question rather than the active session — this is the FE-side
 * equivalent, so UI gating doesn't hide actions the BE would allow anyway.
 */
export const isHomeCompanyAdminOf = (
  user: User | null | undefined,
  companyId: string | null | undefined,
): boolean => {
  if (!user || !companyId) return false;
  return !!user.companies?.some(
    (c) => c.companyId === companyId && c.role === UserRole.COMPANY_ADMIN && !c.isExternal && c.isActive,
  );
};

/**
 * Scans `user.companies[]` for the entry where this person is the
 * (active, non-external) company_admin of their OWN provider company —
 * i.e. their home company, regardless of any other company they're
 * externally lent to (and regardless of which company is currently active
 * for them). Unlike `isHomeCompanyAdminOf`, this doesn't take a target
 * company id — it's used to answer "is this person, in general, a
 * provider's principal admin?" for an arbitrary row in a list (not just the
 * logged-in viewer), e.g. to relabel "Colaborador" as "Administrador
 * Proveedor" wherever they show up as an externally-lent worker.
 *
 * Requires `companies[].company` to be populated with at least
 * `companyType` — true for endpoints like `GET /users` (userController) but
 * NOT for every list endpoint, so callers should confirm their data source
 * populates it before relying on this.
 */
export const getHomeProviderAdminCompany = (
  user: Pick<User, 'companies'> | null | undefined,
): { id: string; name: string } | null => {
  const entry = (user?.companies || []).find((c) => {
    if (c.isExternal || c.isActive === false) return false;
    if (c.role !== UserRole.COMPANY_ADMIN) return false;
    const company = typeof c.company === 'object' ? c.company : null;
    return company?.companyType === 'proveedor';
  });
  if (!entry) return null;
  const company = typeof entry.company === 'object' ? entry.company : null;
  return company ? { id: company._id, name: company.name } : null;
};

/**
 * Get role category label for display
 */
export const getRoleCategoryLabel = (role: UserRole): string => {
  if (isPlatformRole(role)) return 'Plataforma';
  if (isClientRole(role)) return 'Empresa';
  if (isOperationsRole(role)) return 'Operaciones';
  return 'Desconocido';
};

/**
 * Get user-friendly role name
 */
export const getRoleDisplayName = (role: UserRole): string => {
  const roleNames: Record<UserRole, string> = {
    [UserRole.SUPER_ADMIN]: 'Super Administrador',
    [UserRole.SAFETY_STAFF]: 'Soporte Monitor',
    [UserRole.COMPANY_ADMIN]: 'Administrador de Empresa',
    [UserRole.CLIENT_APPROVER]: 'Aprobador de Empresa',
    [UserRole.CLIENT_STAFF]: 'Colaborador',
    [UserRole.SECURITY]: 'Seguridad',
  };
  return roleNames[role] || role;
};
